import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, from } from 'rxjs';
import { CompanyOfficeForm } from 'src/app/classes/form-groups/company-office-form';
import { CompanyVendorForm } from 'src/app/classes/form-groups/company-vendor-form';
import { CompanyVendorOfficeForm } from 'src/app/classes/form-groups/company-vendor-office-form';
import { VendorForm } from 'src/app/classes/form-groups/vendor-form';
import {
  CompanyOffice,
  CompanyVendor,
  CompanyVendorOffice
} from 'src/app/classes/models';
import { CompanyOfficesModalComponent } from 'src/app/modals/company-offices-modal/company-offices-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-client-vendor-office-selection',
  templateUrl: './client-vendor-office-selection.component.html',
  styleUrl: './client-vendor-office-selection.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientVendorOfficeSelectionComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);
  constructor(
    private CompanyVendorService: CompanyVendorService,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private UtilityService: UtilityService
  ) {}

  @Input() VendorForm?: FormGroup;

  // Used to prevent loading the template until the Client/Vendor information has been loaded and applied.
  CompanyVendors: CompanyVendor[] = [];

  CompanyVendorForms(): FormArray {
    return this.VendorForm?.get('CompanyVendors') as FormArray;
  }

  getCompanyVendorOffices(form: AbstractControl): FormArray {
    return form.get('CompanyVendorOffices') as FormArray;
  }

  hasOffices(CompanyVendorFormGroup: CompanyVendorForm) {
    const {
      value: { Company }
    } = CompanyVendorFormGroup;

    if (Company) {
      const { CompanyOffices } = Company;
      return CompanyOffices && CompanyOffices.length > 0;
    }

    return false;
  }

  getClientName(CompanyVendorFormGroup: CompanyVendorForm): string {
    return CompanyVendorFormGroup.value.Company?.legalName || 'Unnamed client';
  }

  getClientOfficeCount(CompanyVendorFormGroup: CompanyVendorForm): number {
    return CompanyVendorFormGroup.value.Company?.CompanyOffices?.length ?? 0;
  }

  /**
   * Clearing a client's last office leaves the vendor serving none of that
   * client's locations — a real change to their standing with that client, so
   * it is confirmed rather than left to a stray click. Emits only on an
   * explicit choice; dismissing the dialog leaves the selection alone.
   */
  private confirmClearingLastOffice(): Observable<boolean> {
    const { componentInstance, closed } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', modalDialogClass: 'confirm-modal' }
    );

    Object.assign(componentInstance, {
      title: 'Remove the last office?',
      displayText:
        "This vendor will no longer be assigned to any of this client's offices. You can add offices back at any time.",
      continueButtonText: 'Remove office',
      confirmTone: 'danger'
    });

    return closed;
  }

  deselectCompanyVendorOffice(
    CompanyVendorForm: CompanyVendorForm,
    CompanyVendorOfficeFormGroupIndex: number
  ) {
    const CompanyVendorOfficesFormArray =
      CompanyVendorForm.controls['CompanyVendorOffices'];

    if (!CompanyVendorOfficesFormArray) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client/Vendor offices form control.',
        { returnError: true }
      );
    }

    const removeSelection = () => {
      CompanyVendorOfficesFormArray.removeAt(CompanyVendorOfficeFormGroupIndex);
      CompanyVendorOfficesFormArray.markAsDirty();
    };

    if (CompanyVendorOfficesFormArray.length > 1) {
      removeSelection();
      return;
    }

    this.confirmClearingLastOffice().subscribe((proceed: boolean) => {
      if (proceed) {
        removeSelection();
      }
    });
  }

  chooseCompanyVendorOffices(CompanyVendorForm: CompanyVendorForm) {
    if (!CompanyVendorForm) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot choose client offices. No CompanyVendor form found.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      CompanyOfficesModalComponent,
      { backdrop: 'static', size: 'lg' }
    );

    const { CompanyId, VendorId } = CompanyVendorForm.value;

    if (!CompanyId || !VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot choose client offices. Missing required Client/Vendor Info.',
        { returnError: true }
      );
    }

    const {
      value: { CompanyVendorOffices }
    } = CompanyVendorForm;

    Object.assign(componentInstance, {
      VendorId: VendorId,
      CompanyIds: [CompanyId],
      selectedCompanyOffices: (CompanyVendorOffices || []).map(
        (cvo) => cvo.CompanyOffice
      )
    });

    from(result).subscribe({
      next: (
        CompanyOffices:
          | Required<Pick<CompanyOffice, 'id' | 'CompanyId' | 'officeName'>>[]
          | null
      ) => {
        if (!CompanyOffices) {
          return;
        }

        const applySelection = () => {
          const CompanyVendorOfficesFormArray = CompanyVendorForm.controls[
            'CompanyVendorOffices'
          ] as FormArray;

          CompanyVendorOfficesFormArray.clear();
          // Mark dirty up front: an empty selection pushes nothing below, and
          // the save only sends clients the admin actually changed.
          CompanyVendorOfficesFormArray.markAsDirty();

          CompanyOffices.forEach((co) => {
            const CompanyOfficeFormGroup = new CompanyOfficeForm();
            const CompanyVendorOfficeFormGroup = new CompanyVendorOfficeForm();

            CompanyOfficeFormGroup.patchValue(co);
            CompanyVendorOfficeFormGroup.patchValue({
              CompanyVendorId: CompanyVendorForm.controls['id'].getRawValue(),
              VendorId: VendorId,
              CompanyId: CompanyId,
              CompanyOfficeId: co.id
            });

            CompanyVendorOfficeFormGroup.setControl(
              'CompanyOffice',
              CompanyOfficeFormGroup
            );
            CompanyVendorOfficeFormGroup.markAsDirty();

            if (!CompanyVendorForm.controls['CompanyVendorOffices']) {
              CompanyVendorForm.setControl(
                'CompanyVendorOffices',
                new FormArray([])
              );
            }

            CompanyVendorForm.controls['CompanyVendorOffices']!.push(
              CompanyVendorOfficeFormGroup
            );
            CompanyVendorForm.controls['CompanyVendorOffices']!.markAsDirty();
          });
        };

        const currentSelectionCount = (
          CompanyVendorForm.controls['CompanyVendorOffices'] as FormArray
        ).length;

        if (CompanyOffices.length === 0 && currentSelectionCount > 0) {
          this.confirmClearingLastOffice().subscribe((proceed: boolean) => {
            if (proceed) {
              applySelection();
            }
          });
          return;
        }

        applySelection();
      }
    });
  }

  ngOnInit(): void {
    if (this.VendorForm) {
      const {
        value: { id: VendorId, CompanyVendors }
      } = this.VendorForm;

      if (!VendorId) {
        throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
          returnError: true
        });
      }

      const ClientIds = (CompanyVendors as CompanyVendor[]).map(
        (cv) => cv.CompanyId
      );

      this.CompanyVendorService.getCompanyVendors(
        this.UtilityService.stringifyObjectsInQueryObject({
          attributes: ['id'],
          VendorId,
          CompanyId: { $in: ClientIds },
          CompanyVendorOffice: {
            CompanyOffice: {}
          },
          Company: {
            attributes: ['id'],
            CompanyOffice: {}
          }
        })
      ).subscribe({
        next: (CompanyVendors) => {
          const CompanyVendorFormMap = new Map<number, CompanyVendorForm>([]);
          (this.VendorForm! as VendorForm).controls[
            'CompanyVendors'
          ]?.controls.forEach((CompanyVendorFormGroup) => {
            const {
              value: { id }
            } = CompanyVendorFormGroup;

            if (!id) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Client/Vendor form is missing id.',
                { returnError: true }
              );
            }

            CompanyVendorFormMap.set(id, CompanyVendorFormGroup);
          });

          CompanyVendors.forEach((CompanyVendor) => {
            const {
              id: CompanyVendorId,
              Company,
              CompanyVendorOffices
            } = CompanyVendor;

            if (!CompanyVendorId) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client/Vendor id.',
                { returnError: true }
              );
            }

            if (!Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client information.',
                { returnError: true }
              );
            }

            const CompanyVendorFormGroup =
              CompanyVendorFormMap.get(CompanyVendorId);

            if (!CompanyVendorFormGroup) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client/Vendor form.',
                { returnError: true }
              );
            }

            const {
              controls: { Company: CompanyFormGroup }
            } = CompanyVendorFormGroup;

            if (!CompanyFormGroup) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client form group.',
                { returnError: true }
              );
            }

            const { CompanyOffices } = Company;

            if (CompanyOffices) {
              const CompanyOfficeFormGroups = CompanyOffices.map(
                (CompanyOffice) => {
                  const CompanyOfficeFormGroup = new CompanyOfficeForm();
                  CompanyOfficeFormGroup.patchValue(CompanyOffice);
                  return CompanyOfficeFormGroup;
                }
              );

              CompanyFormGroup.setControl(
                'CompanyOffices',
                new FormArray<CompanyOfficeForm>(CompanyOfficeFormGroups)
              );
              CompanyVendorFormGroup.setControl(
                'CompanyVendorOffices',
                new FormArray<CompanyVendorOfficeForm>([])
              );
            }

            if (CompanyVendorOffices) {
              const CompanyVendorOfficesFormArray =
                CompanyVendorFormGroup.controls['CompanyVendorOffices'];

              if (!CompanyVendorOfficesFormArray) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Client/Vendor form is missing Client/Vendor offices form array.',
                  { returnError: true }
                );
              }

              CompanyVendorOffices.forEach((CompanyVendorOffice) => {
                const CompanyVendorOfficeFormGroup =
                  new CompanyVendorOfficeForm();
                CompanyVendorOfficeFormGroup.patchValue(CompanyVendorOffice);
                CompanyVendorOfficesFormArray.push(
                  CompanyVendorOfficeFormGroup
                );
              });
            }
          });

          this.CompanyVendors.push(...CompanyVendors);
          this._cdr.markForCheck();
        }
      });
    }
  }
}
